import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Clock3,
  Droplets,
  Gauge,
  Leaf,
  Power,
  Waves,
} from 'lucide-react';
import {
  getSoverdiStatusInfo,
  type MetricFreshness,
  type SoverdiSectorView,
} from '../adapters/agriSafeDashboardAdapter';

interface Props {
  sector: SoverdiSectorView;
  showSoilMoisture?: boolean;
  onDetails?: () => void;
  onToggleValve?: () => void;
  compact?: boolean;
}

/** A null metric has no real source — render the em dash, never a fake 0. */
const formatMetric = (value: number | null | undefined, digits: number): string =>
  value === null || value === undefined ? '—' : value.toFixed(digits);

/** Retained stale telemetry is not a current measurement. */
const formatFreshMetric = (
  value: number | null | undefined,
  freshness: MetricFreshness,
  digits: number,
  unit: string,
): string => {
  if (freshness !== 'fresh' || value === null || value === undefined) return '—';
  return `${value.toFixed(digits)} ${unit}`;
};

const formatMetricWithUnit = (
  value: number | null | undefined,
  digits: number,
  unit: string,
): string => {
  const formatted = formatMetric(value, digits);
  return formatted === '—' ? formatted : `${formatted} ${unit}`;
};

const Metric: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  missingUnit?: string;
}> = ({ icon, label, value, missingUnit }) => (
  <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] px-3 py-2.5 min-h-[72px]">
    <div className="flex items-center gap-1.5 text-[11px] text-[var(--text-secondary)]">{icon}<span>{label}</span></div>
    <div className="text-base sm:text-lg font-semibold text-[var(--text-primary)] mt-1">
      {value === '—' && missingUnit ? (
        <>
          <span aria-hidden="true">—</span>
          <span className="sr-only">{`— ${missingUnit}`}</span>
        </>
      ) : value}
    </div>
  </div>
);

const SoverdiSectorKpiCardR7: React.FC<Props> = ({
  sector,
  showSoilMoisture = false,
  onDetails,
  onToggleValve,
  compact = false,
}) => {
  const { t } = useTranslation('common');
  const status = getSoverdiStatusInfo(sector.status);
  const volume = sector.volumeM3;
  const remaining = sector.remainingMinutes;
  const progress = sector.progressPercent;
  const alert = sector.operationalState === 'not_commissioned' || sector.operationalState === 'unknown';

  return (
    <article className={`rounded-2xl border bg-[var(--surface)] shadow-sm ${alert ? 'border-[var(--color-warning)]/70' : 'border-[var(--border-light)]'} ${compact ? 'p-3' : 'p-4'}`} data-testid={`sector-kpi-r7-${sector.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h3 className={`${compact ? 'text-base' : 'text-lg'} font-semibold text-[var(--text-primary)] truncate`}>{sector.displayName}</h3>
          <p className="text-xs text-[var(--text-secondary)] mt-0.5">{t('sectorTechnicalLabel', { site: sector.siteName, code: sector.sectorCode })}</p>
        </div>
        <span className="text-xs font-semibold whitespace-nowrap" style={{ color: status.color }}>{alert ? 'Alerte' : status.label}</span>
      </div>

      <div className={`grid grid-cols-2 ${showSoilMoisture ? 'xl:grid-cols-3' : ''} gap-2`}>
        <Metric icon={<Gauge className="w-3.5 h-3.5" />} label="Pression" value={formatFreshMetric(sector.pressureBar, sector.pressureFreshness, 1, 'bar')} missingUnit="bar" />
        <Metric icon={<Waves className="w-3.5 h-3.5" />} label="Débit" value={formatFreshMetric(sector.flowM3h, sector.flowFreshness, 1, 'm³/h')} missingUnit="m³/h" />
        <Metric icon={<Droplets className="w-3.5 h-3.5" />} label="Volume" value={formatMetricWithUnit(volume, 1, 'm³')} missingUnit="m³" />
        <Metric icon={<Clock3 className="w-3.5 h-3.5" />} label="Restant" value={formatMetric(remaining, 0)} />
        {showSoilMoisture && <Metric icon={<Leaf className="w-3.5 h-3.5" />} label="Humidité du sol" value={formatFreshMetric(sector.soilMoisturePercent, sector.soilMoistureFreshness, 0, '%')} missingUnit="%" />}
        {showSoilMoisture && <Metric icon={<Power className="w-3.5 h-3.5" />} label="Vanne" value={sector.valveState === 'open' ? 'Ouverte' : sector.valveState === 'closed' ? 'Fermée' : 'État inconnu'} />}
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between text-xs text-[var(--text-secondary)] mb-1.5"><span>Cycle en cours</span><strong className="text-[var(--text-primary)]">{progress === null ? '—' : `${progress} %`}</strong></div>
        <div className="h-2 rounded-full bg-[var(--bg-tertiary)] overflow-hidden"><div className="h-full rounded-full bg-[var(--color-success)] transition-all" style={{ width: progress === null ? '0%' : `${progress}%` }} /></div>
      </div>

      <div className="mt-3 rounded-xl bg-[var(--bg-tertiary)] px-3 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium text-[var(--text-primary)]"><Droplets className="w-4 h-4" />Irrigation</div>
        <span className={`text-xs font-semibold ${sector.valveState === 'open' ? 'text-[var(--color-success)]' : 'text-[var(--text-secondary)]'}`}>{sector.valveState === 'open' ? 'Ouvert' : sector.valveState === 'closed' ? 'Fermé' : 'État inconnu'}</span>
      </div>

      {(onDetails || onToggleValve) && (
        <div className="grid grid-cols-2 gap-2 mt-3">
          {onToggleValve && <button type="button" onClick={onToggleValve} disabled className="min-h-[42px] rounded-lg border border-[var(--border-light)] text-sm font-semibold text-[var(--text-primary)] disabled:opacity-40">{sector.valveState === 'open' ? 'Fermer' : 'Ouvrir'}</button>}
          {onDetails && <button type="button" onClick={onDetails} className={`${onToggleValve ? '' : 'col-span-2'} min-h-[42px] rounded-lg bg-[var(--color-primary)] text-white text-sm font-semibold`}>Détails</button>}
        </div>
      )}
    </article>
  );
};

export default SoverdiSectorKpiCardR7;
